// @ts-nocheck

import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { css } from 'styled-components';

type CSSVariablesContextProps = {
  styleSheet: unknown;
  setProperties: (nextProperties: unknown) => void;
  deleteProperty: (...properties: Array<string>) => void;
};

export const CSSVariablesContext = createContext<CSSVariablesContextProps>({});

export const useCSSVariablesContext = () => useContext(CSSVariablesContext);

type CSSVariablesContextProviderProps = {
  children: React.ReactNode;
};

/**
 * This context provider is used to be able to consume CSS variables even through portals since most of the time in
 * Winabet, when we use createPortal it's instanciating its children inside a top root div (outside current css vars
 * declaration hierarchy scope) and we are not able to use the css var declared by React parents of the portal.
 * */

const CSSVariablesContextProvider = ({ children }: CSSVariablesContextProviderProps) => {
  const [map, setMap] = useState({});
  const [styleSheet, setStyleSheet] = useState(css``);

  const setProperties = useCallback((nextProperties) => {
    if (typeof nextProperties === 'object') {
      setMap((prev) => ({ ...prev, ...nextProperties }));
    }
  }, []);

  const deleteProperty = useCallback((...keys: Array<string>) => {
    setMap((prev) => {
      const isKeyValid = (key) => typeof key === 'string' && prev[key] !== undefined;
      const shouldChange = !!keys.filter(isKeyValid).length;

      if (shouldChange) {
        const next = { ...prev };

        for (const key of keys) {
          if (isKeyValid(key)) {
            delete next[key];
          }
        }

        return next;
      }

      return prev;
    });
  }, []);

  useLayoutEffect(() => {
    const keys = Object.keys(map);
    const stringifiedProperties = keys.map((key) => `${key}: ${map[key]};`).join('\n');

    setStyleSheet(css`
      ${stringifiedProperties}
    `);
  }, [map]);

  const forwardedState = useMemo(
    () => ({
      styleSheet,
      setProperties,
      deleteProperty,
    }),
    [setProperties, styleSheet, deleteProperty],
  );

  return <CSSVariablesContext.Provider value={forwardedState}>{children}</CSSVariablesContext.Provider>;
};

export default CSSVariablesContextProvider;
